import {
    activeSiteConfig,
    activeSiteCurrency,
    getLanguageFromLocale,
} from "@config/site/site-config";
import { PaymentState } from "@graphql/generated/types";
import type { Cart, LineItem, Money } from "@graphql/generated/types";
import type {
    AlgoliaItem,
    AnalyticDefaultItem,
    AnalyticsExtendedItem,
    ListData,
} from "@lib/analytics/analyticsTypes";
import { AlgoliaEventMethods, AnalyticsListTypes, EventTypes } from "@lib/analytics/analyticsTypes";
import { getAlgoliaUserToken } from "@lib/analytics/getAlgoliaUserToken";
import { getImperialSizeForProduct } from "@lib/analytics/getImperialSizeForProduct";
import { isAnalyticsCookiesAllowed } from "@lib/analytics/isAnalyticsCookiesAllowed";
import { sha256 } from "@lib/analytics/sha256";
import type { Product, ProductVariant } from "@lib/product/types";
import { Attributes, CustomAttributes } from "@lib/enums/ProductAttributes";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { getMoneyFromCentsAndFractions } from "@lib/utils/moneyUtils";
import type { Variant } from "@lib/wrappers/algolia/types";
import { getAlgoliaIndexName } from "@lib/wrappers/algolia/helpers";
import type { Me } from "@store/index";
import type { Hit } from "instantsearch.js";
import { isEqual } from "lodash-es";
import type { Session } from "next-auth";
import aa from "search-insights";

class AnalyticsClient {
    locale: string;
    session: Session;
    me: Me;
    algoliaIndexName: string = "";
    lastSavedEvents: Map<EventTypes | AlgoliaEventMethods, any> = new Map();
    selectedItems: Map<
        string,
        ListData & {
            lastAppliedFilter: { filterName: string; filterCategory: string };
            lastAppliedSorting?: { sorting: string };
        }
    > = new Map();
    lastAppliedSorting: { sorting: string };
    lastAppliedFilter: { filterName: string; filterCategory: string; facetName: string };
    lastAppliedSearch: string;
    lastSelectedPromotion: {
        promotionName: string;
        promotionId: string;
    } = {
        promotionName: undefined,
        promotionId: undefined,
    };
    currencyCode: string;
    isCookiesAccepted: boolean = false;
    temporaryEvents: any[] = [];
    lastVisitedUrl: string = "";
    algoliaQueries: { [key: string]: string } = {};

    saveEvent(
        event: { event: EventTypes | AlgoliaEventMethods; [key: string]: any },
        shouldCheckIfSaved = true
    ) {
        if (shouldCheckIfSaved && this.isAlreadyPushed(event.event, event)) {
            return;
        }
        this.savePushedEvent(event.event, event);

        if (this.isCookiesAccepted) {
            window.dataLayer && window.dataLayer.push(event);
        } else {
            this.temporaryEvents.push(event);
        }
    }

    saveEcommerceEvent(event: { event: EventTypes | AlgoliaEventMethods; [key: string]: any }) {
        if (this.isAlreadyPushed(event.event, event)) {
            return;
        }

        this.savePushedEvent(event.event, this.cleanObject(event));

        if (this.isCookiesAccepted) {
            window.dataLayer &&
                window.dataLayer.push({ ecommerce: null }) &&
                window.dataLayer.push(event);
        } else {
            this.temporaryEvents.push(event);
        }
    }

    getMoneyFromItem = (centAmount: number, fractionDigits: number) => {
        return getMoneyFromCentsAndFractions(centAmount ?? 0, fractionDigits ?? 0);
    };

    getCartValue = (items: LineItem[]) => {
        return parseFloat(
            items
                .reduce(
                    (acc, item) =>
                        acc +
                        this.getMoneyFromItem(
                            item.price?.value?.centAmount,
                            item.price?.value?.fractionDigits
                        ) *
                            item.quantity,
                    0
                )
                .toFixed(2)
        );
    };

    mapLineItemToEventItem = (item: LineItem, index?: number): AnalyticsExtendedItem => {
        const params = new URLSearchParams(window.location.href);

        const savedListingData = this.selectedItems.get(item.productKey);

        const lineItemPrice: Money = getProductAttributeValue(
            item.custom.customFieldsRaw,
            CustomAttributes.LINE_ITEM_PRICE
        );
        const lineItemDiscounted: Money = getProductAttributeValue(
            item.custom.customFieldsRaw,
            CustomAttributes.LINE_ITEM_DISCOUNTED_PRICE
        );
        const includedDiscounts =
            item.discountedPricePerQuantity[0]?.discountedPrice?.includedDiscounts ?? [];
        const discountkey =
            includedDiscounts.length > 0 ? (includedDiscounts[0]?.discount?.key ?? "") : "";

        return {
            item_id: item.id,
            item_variant: item.productKey,
            item_name: item.name,
            promotion_name: this.lastSelectedPromotion.promotionName,
            promotion_id: this.lastSelectedPromotion.promotionId,
            item_list_name: savedListingData?.listName,
            item_list_id: savedListingData?.listId,
            item_category: getProductAttributeValue(
                item.variant?.attributesRaw,
                Attributes.Division,
                this.locale
            ),
            item_category2: getProductAttributeValue(
                item.variant?.attributesRaw,
                Attributes.collectionType,
                this.locale
            ),
            item_category3: getProductAttributeValue(
                item.variant?.attributesRaw,
                Attributes.collectionClass,
                this.locale
            ),
            item_brand: "ECCO",
            currency: this.currencyCode,
            price: getMoneyFromCentsAndFractions(
                item.price?.value?.centAmount ?? 0,
                item.price?.value?.fractionDigits ?? 0
            ),
            quantity: item.quantity,
            index: savedListingData?.position ?? index ?? 0,
            discount:
                lineItemDiscounted?.centAmount && lineItemPrice?.centAmount
                    ? getMoneyFromCentsAndFractions(
                          lineItemPrice.centAmount - lineItemDiscounted.centAmount,
                          lineItemPrice.fractionDigits
                      )
                    : 0,
            coupon: discountkey,
            colour: getProductAttributeValue(
                item.variant?.attributesRaw,
                Attributes.dominantColor,
                this.locale
            ),
            variant_price: this.getMoneyFromItem(
                item.price?.value?.centAmount,
                item.price?.value?.fractionDigits
            ),
            full_price: lineItemPrice
                ? getMoneyFromCentsAndFractions(
                      lineItemPrice.centAmount,
                      lineItemPrice.fractionDigits
                  )
                : 0,
            default_variant: item.productKey,
            size: getProductAttributeValue(item.variant?.attributesRaw, Attributes.size),
            size_imperial: getImperialSizeForProduct(item.variant),
            size_type: params.get("system") === "us" ? "Imperial" : "Metric",
            barcode: item.variant?.sku,
            stock_count_online: item.variant?.availability?.channels?.total ?? 0,
            stock_count_offline: undefined,
            on_sale: undefined,
            label_name: undefined,
            label_source: undefined,
            source: undefined,
        };
    };

    cleanObject<T extends Record<string, any>>(obj: T): Partial<T> {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== "") {
                acc[key as keyof T] = value;
            }
            return acc;
        }, {} as Partial<T>);
    }

    saveSearch = (searchTerm) => {
        this.lastAppliedSearch = searchTerm;
    };

    mapCommercetoolsItemToEventItem = (
        item: Product,
        variant?: ProductVariant
    ): AnalyticDefaultItem | AnalyticsExtendedItem => {
        const savedListingData = this.selectedItems.get(item.sku);
        const params = new URLSearchParams(window.location.href);

        return {
            item_id: item.id,
            item_variant: item.sku,
            item_name: item.name,
            promotion_name: this.lastSelectedPromotion.promotionName,
            promotion_id: this.lastSelectedPromotion.promotionId,
            colour: getProductAttributeValue(
                item.attributes,
                Attributes.dominantColor,
                this.locale
            ),
            item_list_name: savedListingData?.listName,
            item_list_id: savedListingData?.listId,
            item_category: getProductAttributeValue(
                item.attributes,
                Attributes.Division,
                this.locale
            ),
            item_category2: getProductAttributeValue(
                item.attributes,
                Attributes.collectionType,
                this.locale
            ),
            item_category3: getProductAttributeValue(
                item.attributes,
                Attributes.collectionClass,
                this.locale
            ),
            item_brand: "ECCO",
            currency: this.currencyCode,
            price: this.getMoneyFromItem(item.price.centAmount, item.price.fractionDigits),
            quantity: 1,
            index: savedListingData?.position ?? 0,
            discount: 0,
            coupon: "",
            default_variant: item.sku,
            size: getProductAttributeValue(variant?.attributesRaw, Attributes.size) ?? "",
            size_imperial: getImperialSizeForProduct(variant),
            size_type: params.get("system") === "us" ? "Imperial" : "Metric",
            barcode: variant?.sku,
        };
    };

    mapAlgoliaItemToEventItem = (
        item: Hit<AlgoliaItem>,
        listData?: ListData
    ): AnalyticDefaultItem => {
        const {
            listName = "",
            listId = "loaded",
            position = 0,
            recommendationCartItemId = null,
            recommendationCartItemName = null,
        } = listData;

        return {
            item_id: item.objectID,
            item_variant: item.articleNumber,
            item_name: item.name,
            promotion_name: this.lastSelectedPromotion.promotionName,
            promotion_id: this.lastSelectedPromotion.promotionId,
            item_list_name: listName,
            item_list_id: listId,
            item_category: item.division ?? "",
            item_category2: item.collectionType ?? "",
            item_category3: item.collectionClass,
            item_brand: "ECCO",
            currency: this.currencyCode,
            price: item.price,
            quantity: 1,
            index: position,
            discount: item.hasDiscount ? item.discountPercentage : 0,
            coupon: "",
            barcode: item.objectID,
            recommendation_cart_item_id: recommendationCartItemId,
            recommendation_cart_item_name: recommendationCartItemName,
        };
    };

    init(locale: string, me: Me, session: Session) {
        this.me = me;
        this.locale = locale;
        this.currencyCode = activeSiteCurrency;
        this.session = session;
        this.algoliaIndexName = getAlgoliaIndexName(locale);

        aa(AlgoliaEventMethods.init, {
            appId: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
            apiKey: process.env.NEXT_PUBLIC_ALGOLIA_API_KEY,
            useCookie: !!me?.customer?.id || isAnalyticsCookiesAllowed(),
            authenticatedUserToken: me?.customer?.id,
            userToken: getAlgoliaUserToken(me),
        });
    }

    handleCookiesAccepted() {
        this.isCookiesAccepted = true;

        this.temporaryEvents.forEach((event) => {
            window.dataLayer && window.dataLayer.push(event);
        });

        this.temporaryEvents = [];
        aa(AlgoliaEventMethods.init, {
            appId: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
            apiKey: process.env.NEXT_PUBLIC_ALGOLIA_API_KEY,
            useCookie: !!this.me?.customer?.id || isAnalyticsCookiesAllowed(),
            authenticatedUserToken: this.me?.customer?.id,
            userToken: getAlgoliaUserToken(this.me),
        });
    }

    savePushedEvent(eventType: EventTypes | AlgoliaEventMethods, item: any) {
        const savedData = this.lastSavedEvents.get(eventType) ?? [];
        this.lastSavedEvents.set(eventType, [...savedData, item]);
    }

    isAlreadyPushed(eventType: EventTypes | AlgoliaEventMethods, item: any) {
        const lastSavedEvents = this.lastSavedEvents.get(eventType) ?? [];
        return lastSavedEvents.find((it) =>
            isEqual(
                { ...item, "gtm.uniqueEventId": undefined },
                {
                    ...it,
                    "gtm.uniqueEventId": undefined,
                }
            )
        );
    }

    pageView = (url, pageType) => {
        Logger.info(ServiceType.WEB, "AnalyticsClient.pageView", {
            tag: LogTag.ANALYTICS,
            url,
            pageType,
        });
        this.saveEvent({
            event: EventTypes.pageView,
            new_url: url,
            previous_url: this.lastVisitedUrl,
            content_group: pageType,
        });
        this.lastVisitedUrl = url;
    };

    updateAnalyticsClientSettings(me: Me, session: Session) {
        this.me = me;
        this.session = session;
    }

    viewItem = async (item: Product) => {
        if (!item) {
            return;
        }

        try {
            this.saveEcommerceEvent({
                event: EventTypes.viewItem,
                ecommerce: {
                    value: this.getMoneyFromItem(item.price.centAmount, item.price.fractionDigits),
                    event_id: await sha256(Math.random + Date.now().toString()),
                    items: [this.mapCommercetoolsItemToEventItem(item)],
                },
            });
            this.lastAppliedFilter &&
                aa(AlgoliaEventMethods.convertedFilters, {
                    index: this.algoliaIndexName,
                    eventName: AlgoliaEventMethods.convertedFilters,
                    filters: [
                        `${this.lastAppliedFilter.filterName}:${this.lastAppliedFilter.filterCategory}`,
                    ],
                });
        } catch (e) {
            this.logError("AnalyticsClient.viewItem error", e);
        }
    };

    viewItemsFromRecommendations = async (items: Hit[]) => {
        if (items.length === 0) {
            return;
        }
        try {
            while (items.length) {
                this.saveEcommerceEvent({
                    event: EventTypes.viewItemsFromCartRecommendations,
                    ecommerce: {
                        value: items.reduce((acc, it) => acc + it.price, 0),
                        items: items.map((it: Hit<AlgoliaItem>) =>
                            this.mapAlgoliaItemToEventItem(it)
                        ),
                    },
                });
            }
            aa(AlgoliaEventMethods.viewedObjectIDs, {
                index: this.algoliaIndexName,
                eventName: AlgoliaEventMethods.viewedObjectIDs,
                objectIDs: items.map((it) => it.objectID),
            });
        } catch (e) {
            this.logError("AnalyticsClient.viewItemsFromRecommendations error", e);
        }
    };

    selectItem(item: AlgoliaItem & { position?: number }, listData: ListData, queryID?: string) {
        if (!item.objectID) {
            return;
        }

        if (queryID) {
            this.algoliaQueries[item.objectID] = queryID;
        }

        try {
            const position = item.position ? item.position : listData.position + 1;
            this.selectedItems.set(item.objectID, {
                ...listData,
                listId: listData?.listId ?? "clicked",
                position,
                lastAppliedFilter:
                    listData.listName === AnalyticsListTypes.plp
                        ? this.lastAppliedFilter
                        : undefined,
                lastAppliedSorting:
                    listData.listName === AnalyticsListTypes.plp
                        ? this.lastAppliedSorting
                        : undefined,
            });
            this.saveEcommerceEvent({
                event: EventTypes.selectItem,
                ecommerce: {
                    value: item.price,
                    items: [
                        this.mapAlgoliaItemToEventItem(
                            { ...item, __position: position, __queryID: queryID },
                            { ...listData, listId: listData?.listId ?? "clicked" }
                        ),
                    ],
                },
            });

            aa(
                queryID
                    ? AlgoliaEventMethods.clickedObjectIDsAfterSearch
                    : AlgoliaEventMethods.clickedObjectIDs,
                {
                    index: this.algoliaIndexName,
                    eventName: queryID
                        ? AlgoliaEventMethods.clickedObjectIDsAfterSearch
                        : AlgoliaEventMethods.clickedObjectIDs,
                    objectIDs: [item.objectID],
                    positions: queryID ? [position] : undefined,
                    queryID: queryID,
                }
            );
        } catch (e) {
            this.logError("AnalyticsClient.selectItem error", e);
        }
    }

    selectItemFromRecommendations = async (item: Hit<AlgoliaItem>) => {
        if (!item) {
            return;
        }
        try {
            this.saveEcommerceEvent({
                event: EventTypes.selectItemFromCartRecommendations,
                value: item.price,
                event_id: await sha256(EventTypes.addToCart + Math.random + Date.now().toString()),
                items: [this.mapAlgoliaItemToEventItem(item)],
            });
            aa(AlgoliaEventMethods.clickedObjectIDs, {
                index: this.algoliaIndexName,
                eventName: AlgoliaEventMethods.clickedObjectIDs,
                objectIDs: [item.objectID],
            });
        } catch (e) {
            this.logError("AnalyticsClient.selectItemFromRecommendations error", e);
        }
    };

    productAvailability(items: Variant[]) {
        if (!items?.length) {
            return;
        }
        try {
            const getCountLabel = (count) => {
                if (count == 0) {
                    return "Out of Stock";
                }
                const ranges = [
                    { min: 0, max: 5, label: "0-5" },
                    { min: 6, max: 10, label: "5-10" },
                    { min: 11, max: 20, label: "10-20" },
                    { min: 21, max: 30, label: "20-30" },
                    { min: 31, max: 50, label: "30-50" },
                    { min: 50, max: Infinity, label: "Over 50" },
                ];

                return (
                    ranges.find((it) => it.min <= count && count <= it.max).label ?? "Out of Stock"
                );
            };

            this.saveEcommerceEvent({
                event: EventTypes.productAvailability,
                stock_count_online: items.reduce((acc, item) => {
                    acc[item.size] = getCountLabel(item.availableQuantity);
                    return acc;
                }, {}),
                in_stock_size: items.filter((it) => it.hasStock).map((it) => it.size),
                out_of_stock_size: items.filter((it) => !it.hasStock).map((it) => it.size),
            });
        } catch (e) {
            Logger.warn(ServiceType.WEB, "AnalyticsClient.productAvailability error", {
                tag: LogTag.ANALYTICS,
                error: e,
            });
        }
    }

    loadItemsList(items: AlgoliaItem[], listData: ListData = {}) {
        if (!items?.length) {
            return;
        }
        try {
            const otherItems = [...items];
            let batchNumber = 0;
            while (otherItems.length) {
                const slicedItems = otherItems.splice(0, 15);
                this.saveEcommerceEvent({
                    event: EventTypes.loadItemsList,
                    ecommerce: {
                        value: slicedItems.reduce((acc, it) => acc + it.price, 0),
                        items: slicedItems.map((it: Hit<AlgoliaItem>, index) =>
                            this.mapAlgoliaItemToEventItem(it, {
                                ...listData,
                                listId: listData?.listId ?? "loaded",
                                position: batchNumber * 15 + index,
                            })
                        ),
                    },
                });
                batchNumber += 1;
            }
        } catch (e) {
            Logger.warn(ServiceType.WEB, "AnalyticsClient.loadItemsList error", {
                tag: LogTag.ANALYTICS,
                error: e,
            });
        }
    }

    viewItemsList(items: AlgoliaItem[], listData: ListData, originalList: AlgoliaItem[]) {
        if (!items?.length) {
            return;
        }

        try {
            const otherItems = [...items];
            while (otherItems.length) {
                const slicedItems = otherItems.splice(0, 15);
                this.saveEcommerceEvent({
                    event: EventTypes.viewItemsList,
                    ecommerce: {
                        value: slicedItems.reduce((acc, it) => acc + it.price, 0),
                        items: slicedItems.map((it: Hit<AlgoliaItem>) =>
                            this.mapAlgoliaItemToEventItem(it, {
                                ...listData,
                                listId: listData.listId ?? "viewed",
                                position: originalList.findIndex(
                                    (originalItem) => originalItem.objectID === it.objectID
                                ),
                                ...this.lastAppliedFilter,
                                ...this.lastAppliedSorting,
                            })
                        ),
                    },
                });

                aa(AlgoliaEventMethods.viewedObjectIDs, {
                    index: this.algoliaIndexName,
                    eventName: AlgoliaEventMethods.viewedObjectIDs,
                    objectIDs: items.map((it) => it.objectID),
                });
            }
        } catch (e) {
            this.logError("AnalyticsClient.viewItemsList error", e);
        }
    }

    addToCartLineItem = async (item: LineItem) => {
        if (!item) {
            return;
        }

        try {
            this.saveEcommerceEvent({
                event: EventTypes.addToCart,
                ecommerce: {
                    value: this.getMoneyFromItem(
                        item.price?.value?.centAmount,
                        item.price?.value?.fractionDigits
                    ),
                    event_id: await sha256(
                        EventTypes.addToCart + Math.random + Date.now().toString()
                    ),
                    items: [this.mapLineItemToEventItem(item)],
                },
            });

            const isAfterSearch = this.algoliaQueries[item.key];

            aa(
                isAfterSearch
                    ? AlgoliaEventMethods.addedToCartObjectIDsAfterSearch
                    : AlgoliaEventMethods.addedToCartObjectIDs,
                {
                    index: this.algoliaIndexName,
                    objectIDs: [item.key],
                    eventName: isAfterSearch
                        ? AlgoliaEventMethods.addedToCartObjectIDsAfterSearch
                        : AlgoliaEventMethods.addedToCartObjectIDs,
                    value: this.getMoneyFromItem(
                        item.price?.value?.centAmount,
                        item.price?.value?.fractionDigits
                    ),
                    currency: activeSiteConfig.currency,
                    queryID: this.algoliaQueries[item.key],
                }
            );
        } catch (e) {
            this.logError("AnalyticsClient.addToCartLineItem error", e);
        }
    };

    addToCart = async (item: Product, variant: ProductVariant) => {
        if (!item) {
            return;
        }

        try {
            this.saveEcommerceEvent({
                event: EventTypes.addToCart,
                ecommerce: {
                    value: this.getMoneyFromItem(
                        item.price?.centAmount,
                        item.price?.fractionDigits
                    ),
                    event_id: await sha256(
                        EventTypes.addToCart + Math.random + Date.now().toString()
                    ),
                    items: [this.mapCommercetoolsItemToEventItem(item, variant)],
                },
            });

            const isAfterSearch = this.algoliaQueries[item.sku];

            aa(
                isAfterSearch
                    ? AlgoliaEventMethods.addedToCartObjectIDsAfterSearch
                    : AlgoliaEventMethods.addedToCartObjectIDs,
                {
                    index: this.algoliaIndexName,
                    objectIDs: [item.sku],
                    eventName: isAfterSearch
                        ? AlgoliaEventMethods.addedToCartObjectIDsAfterSearch
                        : AlgoliaEventMethods.addedToCartObjectIDs,
                    value: this.getMoneyFromItem(
                        item.price?.centAmount,
                        item.price?.fractionDigits
                    ),
                    currency: activeSiteConfig.currency,
                    queryID: this.algoliaQueries[item.sku],
                }
            );
        } catch (e) {
            this.logError("AnalyticsClient.addToCart error", e);
        }
    };

    addToCartFromShoppingBag = async (item: LineItem) => {
        if (!item) {
            return;
        }
        try {
            this.saveEcommerceEvent({
                event: EventTypes.addToCart,
                value: this.getMoneyFromItem(
                    item.price?.value?.centAmount,
                    item.price?.value?.fractionDigits
                ),
                event_id: await sha256(EventTypes.addToCart + Math.random + Date.now().toString()),
                items: [this.mapLineItemToEventItem(item)],
            });
        } catch (e) {
            this.logError("AnalyticsClient.addToCart error", e);
        }
    };

    addToCartFromCartRecommendations = async (item: Hit<AlgoliaItem>, listData: ListData = {}) => {
        if (!item) {
            return;
        }
        try {
            this.saveEcommerceEvent({
                event: EventTypes.addToCart,
                value: item.price,
                event_id: await sha256(EventTypes.addToCart + Math.random + Date.now().toString()),
                items: [this.mapAlgoliaItemToEventItem(item, listData)],
            });
            aa(AlgoliaEventMethods.addedToCartObjectIDs, {
                index: this.algoliaIndexName,
                objectIDs: [item.objectID],
                eventName: AlgoliaEventMethods.addedToCartObjectIDs,
                value: item.price,
                currency: activeSiteConfig.currency,
            });
        } catch (e) {
            this.logError("AnalyticsClient.addToCartFromCartRecommendations error", e);
        }
    };

    addToWishList = async (item: Hit<AlgoliaItem>, listData = {}) => {
        if (!item) {
            return;
        }
        try {
            this.saveEcommerceEvent({
                event: EventTypes.addToWishlist,
                ecommerce: {
                    value: item.price,
                    event_id: await sha256(
                        EventTypes.addToWishlist + Math.random + Date.now().toString()
                    ),
                    items: [
                        this.mapAlgoliaItemToEventItem(item, { ...listData, listId: "clicked" }),
                    ],
                },
            });
        } catch (e) {
            this.logError("AnalyticsClient.addToWishList error", e);
        }
    };

    removeFromWishList = async (item: Hit<AlgoliaItem>, listData = {}) => {
        if (!item) {
            return;
        }
        try {
            this.saveEcommerceEvent({
                event: EventTypes.removeFromWishlist,
                ecommerce: {
                    value: item.price,
                    event_id: await sha256(
                        EventTypes.removeFromWishlist + Math.random + Date.now().toString()
                    ),
                    items: [
                        this.mapAlgoliaItemToEventItem(item, { ...listData, listId: "clicked" }),
                    ],
                },
            });
        } catch (e) {
            this.logError("AnalyticsClient.removeFromWishList error", e);
        }
    };

    addCTProductToWishList = async (item: Product) => {
        if (!item) {
            return;
        }
        try {
            this.saveEcommerceEvent({
                event: EventTypes.addToWishlist,
                ecommerce: {
                    value: this.getMoneyFromItem(item.price.centAmount, item.price.fractionDigits),
                    event_id: await sha256(
                        EventTypes.addToWishlist + Math.random + Date.now().toString()
                    ),
                    items: [this.mapCommercetoolsItemToEventItem(item)],
                },
            });
        } catch (e) {
            this.logError("AnalyticsClient.addCTProductToWishList error", e);
        }
    };

    removeCTProductFromWishlist = async (item: LineItem) => {
        if (!item) {
            return;
        }

        try {
            this.saveEcommerceEvent({
                event: EventTypes.removeFromWishlist,
                ecommerce: {
                    value: this.getMoneyFromItem(
                        item.price?.value?.centAmount,
                        item.price?.value?.fractionDigits
                    ),
                    event_id: await sha256(
                        EventTypes.removeFromWishlist + Math.random + Date.now().toString()
                    ),
                    items: [this.mapLineItemToEventItem(item)],
                },
            });
        } catch (e) {
            this.logError("AnalyticsClient.removeFromWishlist error", e);
        }
    };

    removeFromCart(item: LineItem, index) {
        if (!item) {
            return;
        }
        try {
            this.saveEcommerceEvent({
                event: EventTypes.removeFromCart,
                ecommerce: {
                    value: this.getMoneyFromItem(
                        item.price?.value?.centAmount,
                        item.price?.value?.fractionDigits
                    ),
                    items: [this.mapLineItemToEventItem(item, index)],
                },
            });
        } catch (e) {
            this.logError("AnalyticsClient.removeFromCart error", e);
        }
    }

    viewCart = async (items: LineItem[]) => {
        if (!items) {
            return;
        }

        try {
            this.saveEcommerceEvent({
                event: EventTypes.viewCart,
                ecommerce: {
                    value: this.getCartValue(items),
                    event_id: await sha256(
                        EventTypes.viewCart + Math.random + Date.now().toString()
                    ),
                    items: items.map(this.mapLineItemToEventItem),
                },
            });
        } catch (e) {
            this.logError("AnalyticsClient.viewCart error", e);
        }
    };

    beginCheckout = async (items: LineItem[]) => {
        if (!items) {
            return;
        }

        try {
            const mappedItems = items.map(this.mapLineItemToEventItem);
            this.saveEcommerceEvent({
                event: EventTypes.beginCheckout,
                ecommerce: {
                    value: this.getCartValue(items),
                    event_id: await sha256(
                        EventTypes.beginCheckout + Math.random + Date.now().toString()
                    ),
                    coupon: this?.me?.cartInfo?.cart?.discountCodes
                        .map((it) => it.discountCode.code)
                        .join(", "),
                    items: mappedItems,
                },
            });
        } catch (e) {
            this.logError("AnalyticsClient.beginCheckout error", e);
        }
    };

    addShippingInfo(items: LineItem[], shippingMethod?: string) {
        if (!items) {
            return;
        }

        try {
            this.saveEcommerceEvent({
                event: EventTypes.addShippingInfo,
                ecommerce: {
                    value: this.getCartValue(items),
                    shipping_tier: shippingMethod ?? undefined,
                    coupon: this?.me?.cartInfo?.cart?.discountCodes
                        .map((it) => it.discountCode.code)
                        .join(", "),
                    items: items.map(this.mapLineItemToEventItem),
                },
            });
        } catch (e) {
            this.logError("AnalyticsClient.addShippingInfo error", e);
        }
    }

    addPaymentInfo(items: LineItem[], paymentType?: string) {
        if (!items) {
            return;
        }

        try {
            this.saveEcommerceEvent({
                event: EventTypes.addPaymentInfo,
                ecommerce: {
                    value: this.getCartValue(items),
                    payment_type: paymentType ?? undefined,
                    coupon: this?.me?.cartInfo?.cart?.discountCodes
                        .map((it) => it.discountCode.code)
                        .join(", "),
                    items: items.map(this.mapLineItemToEventItem),
                },
            });
        } catch (e) {
            this.logError("AnalyticsClient.addPaymentInfo error", e);
        }
    }

    async purchaseAdded(cart: Cart & { orderNumber: string }, paymentMethod?: string) {
        try {
            this.saveEcommerceEvent({
                event: EventTypes.purchase,
                ecommerce: {
                    value: this.getCartValue(cart.lineItems),
                    transaction_id: cart?.orderNumber ?? undefined,
                    affiliation: "Online",
                    visitor_email: cart.shippingAddress.email,
                    visitor_email_sha: await sha256(cart.shippingAddress.email),
                    purchase_payment:
                        paymentMethod ||
                        cart?.paymentInfo?.payments?.[0]?.paymentMethodInfo?.method,
                    purchase_store_id: cart.store.key,
                    purchase_store_name: cart.store.key,
                    visitor_phone_sha256: await sha256(cart.shippingAddress.phone),
                    visitor_street: cart.billingAddress.streetName,
                    visitor_country: cart.billingAddress.country,
                    visitor_city: cart.billingAddress.city,
                    shipping: cart.shippingInfo.shippingMethodName,
                    tax: getMoneyFromCentsAndFractions(
                        cart.taxedPrice.totalTax.centAmount,
                        cart.taxedPrice.totalTax.fractionDigits
                    ),
                    event_id: await sha256(EventTypes.purchase + cart?.orderNumber),
                    items: cart.lineItems.map(this.mapLineItemToEventItem),
                },
            });

            const isAfterSearch = !!Object.keys(this.algoliaQueries).length;

            aa(
                isAfterSearch
                    ? AlgoliaEventMethods.purchasedObjectIDsAfterSearch
                    : AlgoliaEventMethods.purchasedObjectIDs,
                {
                    index: this.algoliaIndexName,
                    objectIDs: cart.lineItems.map((it) => it.productKey),
                    eventName: isAfterSearch
                        ? AlgoliaEventMethods.purchasedObjectIDsAfterSearch
                        : AlgoliaEventMethods.purchasedObjectIDs,
                    value: this.getCartValue(cart.lineItems),
                    currency: activeSiteConfig.currency,
                    objectData: cart.lineItems.map((it) => ({
                        price: parseFloat((it.price.value.centAmount / 100).toFixed(2)),
                        discount: parseFloat(
                            (
                                (getProductAttributeValue(
                                    it?.custom?.customFieldsRaw,
                                    CustomAttributes.LINE_ITEM_PRICE
                                ).centAmount -
                                    getProductAttributeValue(
                                        it?.custom?.customFieldsRaw,
                                        CustomAttributes.LINE_ITEM_DISCOUNTED_PRICE
                                    ).centAmount) /
                                100
                            ).toFixed(2)
                        ),
                        quantity: it.quantity,
                        queryID: this.algoliaQueries?.[it.productKey],
                    })),
                }
            );
            this.algoliaQueries = {};
        } catch (e) {
            this.logError("AnalyticsClient.purchaseAdded error", e);
        }
    }

    filtersView(filters) {
        if (!filters.length) {
            return;
        }
        try {
            const appliedFilters = filters.reduce((acc, item) => {
                item.refinements.forEach((it) => acc.push(`${item.label}: ${it.label}`));
                return acc;
            }, []);

            aa(AlgoliaEventMethods.viewedFilters, {
                filters: appliedFilters.length <= 10 ? appliedFilters : appliedFilters.slice(-10),
                index: this.algoliaIndexName,
                eventName: AlgoliaEventMethods.viewedFilters,
            });
        } catch (e) {
            this.logError("AnalyticsClient.filterView error", e);
        }
    }

    listFilterApplied = (filterName, filterCategory, facetName) => {
        try {
            this.saveEvent({
                event: EventTypes.listFilterApplied,
                filter_category: filterCategory,
                filter_name: filterName,
            });

            aa(AlgoliaEventMethods.clickedFilters, {
                filters: [`${facetName}:${filterCategory}`],
                index: this.algoliaIndexName,
                eventName: AlgoliaEventMethods.clickedFilters,
            });
            this.lastAppliedFilter = { filterName, filterCategory, facetName };
        } catch (e) {
            this.logError("AnalyticsClient.listFilterApplied error", e);
        }
    };

    listSortingApplied = (sorting: string) => {
        try {
            this.saveEvent({
                event: EventTypes.listSortingApplied,
                sorting_applied: sorting,
            });
            this.lastAppliedSorting = { sorting };
        } catch (e) {
            this.logError("AnalyticsClient.listSortingApplied error", e);
        }
    };

    newsletterSubscribe = async (userEmail: string) => {
        try {
            this.saveEvent({
                event: EventTypes.newsletterSubscribe,
                nl_email: userEmail,
                nl_email_sha256: await sha256(userEmail),
                nl_first_name: undefined,
                nl_last_name: undefined,
            });
        } catch (e) {
            this.logError("AnalyticsClient.newsletterSubscribe error", e);
        }
    };

    purchaseStatus = (result, paymentMethod: string) => {
        try {
            this.saveEvent({
                event: EventTypes.purchaseStatus,
                status_order_id: result.orderId,
                status_pay_method: paymentMethod,
                status_category: this.resultCodeToPaymentState(result?.resultCode),
                status_response_code: result?.resultCode,
                status_reason: result.action,
            });
        } catch (e) {
            this.logError("AnalyticsClient.purchaseStatus error", e);
        }
    };

    accountCreate = () => {
        try {
            this.saveEvent({
                event: EventTypes.accountCreate,
            });
        } catch (e) {
            this.logError("AnalyticsClient.accountCreate error", e);
        }
    };

    cartContents = () => {
        if (!this.me?.cartInfo?.cart) {
            return;
        }
        this.saveEvent(
            {
                event: EventTypes.cartContents,
                basket_id: this.me?.cartInfo?.cart?.id,
                basket_url: window.location.hostname + "/shopping-bag",
                cart_size: this.me?.cartInfo?.cart?.lineItems.reduce((acc, it) => {
                    return it.quantity + acc;
                }, 0),
                currency: activeSiteCurrency,
                value: this.getCartValue(this.me?.cartInfo?.cart?.lineItems),
                items: this.me?.cartInfo?.cart?.lineItems.map(this.mapLineItemToEventItem),
            },
            false
        );
    };

    viewPromotion = ({
        image,
        title,
        index,
        id,
    }: {
        image: string;
        title: string;
        index: number;
        id: string;
    }) => {
        const url = image?.startsWith("http") ? image : `https:${image}`;
        this.saveEcommerceEvent({
            event: EventTypes.viewPromotion,
            ecommerce: {
                promotion_id: id,
                promotion_name: title?.trim(),
                creative_slot: index,
                creative_name: url,
            },
        });
    };

    selectPromotion = ({
        image,
        title,
        index,
        id,
    }: {
        image: string;
        title: string;
        index: number;
        id: string;
    }) => {
        this.lastSelectedPromotion = {
            promotionId: id,
            promotionName: title.trim(),
        };

        this.saveEcommerceEvent({
            event: EventTypes.selectPromotion,
            ecommerce: {
                promotion_id: id,
                promotion_name: title.trim(),
                creative_slot: index,
                creative_name: `https:${image}`,
            },
        });
    };

    saveUserData = async (
        user: Me,
        session: Session,
        isAuthenticated: boolean,
        pageType: string
    ) => {
        try {
            const customerId = user?.customer?.id ?? user?.cartInfo?.cart?.anonymousId ?? "";

            const savedSuperUserId = JSON.parse(localStorage.getItem("superUserId"));

            const superId = await sha256(
                (customerId ?? "") + window.navigator.userAgent.toString()
            );

            const customerAddress =
                user?.customer?.defaultShippingAddress ??
                user?.customer?.shippingAddresses?.[0] ??
                user?.cartInfo?.cart?.shippingAddress;
            const userDataEvent = {
                event: EventTypes.userData,
                user_id: isAuthenticated ? user?.customer?.id : session?.anonymousId,
                external_user_id: session?.ucid,
                user_language: getLanguageFromLocale(this.locale),
                super_user_id: savedSuperUserId ?? superId,
                custom_session_id: session?.accessToken,
                content_group: pageType ?? "generic",
                currency: this.currencyCode,
                visitor_gender: undefined,
                visitor_email: customerAddress?.email ?? undefined,
                visitor_phone: customerAddress?.phone ?? undefined,
                visitor_first_name: isAuthenticated ? user?.customer?.firstName : undefined,
                visitor_last_name: isAuthenticated ? user?.customer?.lastName : undefined,
                visitor_email_sha256: customerAddress?.email
                    ? await sha256(customerAddress?.email)
                    : undefined,
                visitor_postal_code: customerAddress?.postalCode,
                visitor_status: isAuthenticated ? "logged-in" : "guest",
                store_purchase: undefined,
                last_purchase: undefined,
                first_purchase: undefined,
                delivery_rate: undefined,
                wishlist_products: user.wishlistInfo?.wishlist?.lineItems?.length ?? 0,
                wishlist_value: user.wishlistInfo?.wishlist?.lineItems?.reduce((acc, it) => {
                    return (
                        acc +
                        getMoneyFromCentsAndFractions(
                            it?.variant?.price?.value?.centAmount,
                            it?.variant?.price?.value?.fractionDigits
                        )
                    );
                }, 0),
                visitor_type: user.customer?.createdAt ? "New User" : undefined,
            };

            user?.customer && sessionStorage.setItem("savedUser", JSON.stringify(user));
            !savedSuperUserId && localStorage.setItem("superUserId", JSON.stringify(superId));
            this.saveEcommerceEvent(userDataEvent);
        } catch (e) {
            this.logError("AnalyticsClient.saveUserData error", e);
        }
    };

    sraOrCcpMiniBagClose(hasRecomendations: boolean, objectID: string) {
        this.saveEvent(
            {
                event: EventTypes.mini_bag_close,
                mini_bag_has_recommendations: hasRecomendations ? "yes" : "no",
                objectID,
            },
            false
        );
    }

    sraOrCcpMiniBagView(hasRecomendations: boolean, objectID: string) {
        this.saveEvent({
            event: EventTypes.mini_bag_view,
            mini_bag_has_recommendations: hasRecomendations ? "yes" : "no",
            objectID,
        });
    }

    sraOrCcpMiniBagScroll(hasRecomendations: boolean, objectID: string) {
        this.saveEvent(
            {
                event: EventTypes.mini_bag_scroll,
                mini_bag_has_recommendations: hasRecomendations ? "yes" : "no",
                objectID: objectID,
            },
            false
        );
    }

    sraOrCcpMiniBagClick(hasRecomendations: boolean, objectID: string) {
        this.saveEvent(
            {
                event: EventTypes.mini_bag_click,
                mini_bag_has_recommendations: hasRecomendations ? "yes" : "no",
                objectID: objectID,
            },
            false
        );
    }

    resultCodeToPaymentState = (resultCode: string) => {
        switch (resultCode) {
            case "Authorised":
                return PaymentState.Paid;
            case "Pending":
            case "Received":
            case "IdentifyShopper":
            case "ChallengeShopper":
                return PaymentState.Pending;
            case "Refused":
            case "Error":
                return PaymentState.Failed;
            default:
                return PaymentState.Failed;
        }
    };

    private logError(message: string, error: Error) {
        Logger.warn(ServiceType.WEB, message, {
            tag: LogTag.ANALYTICS,
            error,
        });
    }

    private static _instance: AnalyticsClient;

    public static getInstance(): AnalyticsClient {
        if (!this._instance) {
            this._instance = new AnalyticsClient();
        }
        return this._instance;
    }
}

const analyticsClient = AnalyticsClient.getInstance();

export { analyticsClient as AnalyticsClient };
